<!-- resources/js/views/agents/AgentManager.vue -->
<template>
  <div class="agent-manager">
    <ChatBot/>
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>مدیریت کارمندهای هوش مصنوعی</h2>
      <button @click="showCreateModal = true" class="btn btn-primary">
        <i class="fas fa-plus"></i> کارمند جدید
      </button>
    </div>

    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary"></div>
      <p>در حال بارگذاری...</p>
    </div>

    <div v-else-if="agents.length === 0" class="text-center py-4">
      <p class="text-muted">هیچ کارمندی یافت نشد</p>
    </div>

    <div v-else class="row">
      <div v-for="agent in agents" :key="agent.id" class="col-md-6 col-lg-4 mb-4">
        <div class="card agent-card" :class="{ 'inactive': !agent.is_active }">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start">
              <h5 class="card-title">
                <i class="fas fa-robot"></i> {{ agent.name }}
              </h5>
              <span class="badge" :class="agent.is_active ? 'badge-success' : 'badge-secondary'">
                {{ agent.is_active ? 'فعال' : 'غیرفعال' }}
              </span>
            </div>

            <p class="card-text text-muted">{{ agent.description || 'بدون توضیحات' }}</p>

            <div class="agent-stats">
              <small class="text-muted">
                <i class="fas fa-tasks"></i> وظایف: {{ agent.tasks_count || 0 }}
              </small>
              <small class="text-muted">
                <i class="fas fa-calendar"></i> {{ formatDate(agent.created_at) }}
              </small>
            </div>

            <div class="agent-actions mt-3">
              <button @click="sendCommand(agent)" class="btn btn-sm btn-info">
                <i class="fas fa-paper-plane"></i> دستور
              </button>
              <button @click="editAgent(agent)" class="btn btn-sm btn-warning">
                <i class="fas fa-edit"></i>
              </button>
              <button @click="toggleAgent(agent)" class="btn btn-sm" :class="agent.is_active ? 'btn-secondary' : 'btn-success'">
                <i class="fas" :class="agent.is_active ? 'fa-pause' : 'fa-play'"></i>
              </button>
              <button @click="deleteAgent(agent)" class="btn btn-sm btn-danger">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- مودال ایجاد کارمند -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h5>ایجاد کارمند جدید</h5>
          <button @click="showCreateModal = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="createAgent">
            <div class="form-group">
              <label>نام کارمند</label>
              <input v-model="newAgent.name" type="text" class="form-control" required />
            </div>
            <div class="form-group">
              <label>توضیحات</label>
              <textarea v-model="newAgent.description" class="form-control" rows="3"></textarea>
            </div>
            <div class="form-group">
              <label>دستور پیش‌فرض</label>
              <input v-model="newAgent.default_command" type="text" class="form-control" placeholder="مثلاً: ارسال پیام به مشتریان" />
            </div>
            <div class="form-group">
              <label>تنظیمات (JSON)</label>
              <textarea v-model="newAgent.config" class="form-control" rows="3" placeholder='{"schedule": "daily", "max_actions": 50}'></textarea>
            </div>
            <button type="submit" class="btn btn-primary btn-block" :disabled="saving">
              {{ saving ? 'در حال ایجاد...' : 'ایجاد کارمند' }}
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- مودال ارسال دستور -->
    <div v-if="showCommandModal" class="modal-overlay" @click.self="showCommandModal = false">
      <div class="modal-content modal-lg">
        <div class="modal-header">
          <h5>ارسال دستور به {{ selectedAgent ? selectedAgent.name : '' }}</h5>
          <button @click="showCommandModal = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>دستور خود را بنویسید</label>
            <textarea v-model="commandText" class="form-control" rows="4" placeholder="مثلاً: به مشتریان وفادار که بیش از ۳۰ روز است خرید نکرده‌اند، پیام تخفیف بفرست..."></textarea>
          </div>
          <button @click="executeCommand" class="btn btn-primary btn-block" :disabled="executing || !commandText">
            {{ executing ? 'در حال اجرا...' : 'اجرای دستور' }}
          </button>

          <div v-if="commandResult" class="result-box mt-3">
            <h6>نتیجه:</h6>
            <pre>{{ JSON.stringify(commandResult, null, 2) }}</pre>
          </div>
        </div>
      </div>
    </div>

    <!-- مودال ویرایش -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="showEditModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h5>ویرایش کارمند</h5>
          <button @click="showEditModal = false" class="close-btn">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="updateAgent">
            <div class="form-group">
              <label>نام کارمند</label>
              <input v-model="editAgentData.name" type="text" class="form-control" required />
            </div>
            <div class="form-group">
              <label>توضیحات</label>
              <textarea v-model="editAgentData.description" class="form-control" rows="3"></textarea>
            </div>
            <div class="form-group">
              <label>دستور پیش‌فرض</label>
              <input v-model="editAgentData.default_command" type="text" class="form-control" />
            </div>
            <div class="form-group">
              <label>تنظیمات (JSON)</label>
              <textarea v-model="editAgentData.config" class="form-control" rows="3"></textarea>
            </div>
            <button type="submit" class="btn btn-primary btn-block" :disabled="saving">
              {{ saving ? 'در حال به‌روزرسانی...' : 'به‌روزرسانی' }}
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ChatBot from "./ChatBot.vue";

export default {
  name: 'AgentManager',
  components: {ChatBot},
  data() {
    return {
      agents: [],
      loading: false,
      saving: false,
      executing: false,
      showCreateModal: false,
      showCommandModal: false,
      showEditModal: false,
      selectedAgent: null,
      commandText: '',
      commandResult: null,
      newAgent: {
        name: '',
        description: '',
        default_command: '',
        config: '{}'
      },
      editAgentData: {
        id: null,
        name: '',
        description: '',
        default_command: '',
        config: '{}'
      }
    }
  },
  mounted() {
    this.loadAgents();
  },
  methods: {
    async loadAgents() {
      this.loading = true;
      try {
        const response = await axios.get('/api/admin/agents');
        this.agents = response.data;
      } catch (error) {
        console.error('Error loading agents:', error);
        this.$toast?.error('خطا در بارگذاری کارمندها');
      } finally {
        this.loading = false;
      }
    },

    async createAgent() {
      this.saving = true;
      try {
        const data = {
          ...this.newAgent,
          config: JSON.parse(this.newAgent.config || '{}')
        };
        await axios.post('/api/admin/agents', data);
        await this.loadAgents();
        this.showCreateModal = false;
        this.newAgent = { name: '', description: '', default_command: '', config: '{}' };
        this.$toast?.success('کارمند با موفقیت ایجاد شد');
      } catch (error) {
        console.error('Error creating agent:', error);
        this.$toast?.error('خطا در ایجاد کارمند');
      } finally {
        this.saving = false;
      }
    },

    async updateAgent() {
      this.saving = true;
      try {
        const data = {
          ...this.editAgentData,
          config: JSON.parse(this.editAgentData.config || '{}')
        };
        await axios.put(`/api/admin/agents/${data.id}`, data);
        await this.loadAgents();
        this.showEditModal = false;
        this.$toast?.success('کارمند با موفقیت به‌روز شد');
      } catch (error) {
        console.error('Error updating agent:', error);
        this.$toast?.error('خطا در به‌روزرسانی کارمند');
      } finally {
        this.saving = false;
      }
    },

    async toggleAgent(agent) {
      try {
        await axios.post(`/api/admin/agents/${agent.id}/toggle`);
        await this.loadAgents();
        this.$toast?.success('وضعیت کارمند تغییر کرد');
      } catch (error) {
        console.error('Error toggling agent:', error);
        this.$toast?.error('خطا در تغییر وضعیت');
      }
    },

    async deleteAgent(agent) {
      if (!confirm(`آیا از حذف کارمند "${agent.name}" مطمئن هستید؟`)) return;

      try {
        await axios.delete(`/api/admin/agents/${agent.id}`);
        await this.loadAgents();
        this.$toast?.success('کارمند حذف شد');
      } catch (error) {
        console.error('Error deleting agent:', error);
        this.$toast?.error('خطا در حذف کارمند');
      }
    },

    sendCommand(agent) {
      this.selectedAgent = agent;
      this.commandText = '';
      this.commandResult = null;
      this.showCommandModal = true;
    },

    async executeCommand() {
      if (!this.commandText || !this.selectedAgent) return;

      this.executing = true;
      this.commandResult = null;

      try {
        const response = await axios.post('/api/agent/command', {
          agent_id: this.selectedAgent.id,
          command: this.commandText
        });
        this.commandResult = response.data;
        this.$toast?.success('دستور با موفقیت اجرا شد');
      } catch (error) {
        console.error('Error executing command:', error);
        this.commandResult = { error: error.response?.data?.message || 'خطا در اجرای دستور' };
        this.$toast?.error('خطا در اجرای دستور');
      } finally {
        this.executing = false;
      }
    },

    editAgent(agent) {
      this.editAgentData = {
        id: agent.id,
        name: agent.name,
        description: agent.description || '',
        default_command: agent.config?.default_command || '',
        config: JSON.stringify(agent.config || {}, null, 2)
      };
      this.showEditModal = true;
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('fa-IR');
    }
  }
}
</script>

<style scoped>
.agent-manager {
  padding: 20px;
}

.agent-card {
  transition: all 0.3s;
  border: 1px solid #e5e7eb;
  height: 100%;
}

.agent-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.1);
}

.agent-card.inactive {
  opacity: 0.6;
}

.agent-stats {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  font-size: 12px;
}

.agent-actions {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.agent-actions .btn {
  flex: 1;
  font-size: 12px;
  padding: 5px 10px;
  min-width: 40px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 500px;
  max-width: 95%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 0;
}

.modal-content.modal-lg {
  width: 700px;
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h5 {
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #9ca3af;
}

.close-btn:hover {
  color: #374151;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  font-weight: 500;
  margin-bottom: 5px;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102,126,234,0.1);
}

.result-box {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  max-height: 300px;
  overflow-y: auto;
}

.result-box pre {
  margin: 0;
  font-size: 12px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a67d8;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-info { background: #17a2b8; color: white; }
.btn-warning { background: #ffc107; color: #212529; }
.btn-danger { background: #dc3545; color: white; }
.btn-secondary { background: #6c757d; color: white; }
.btn-success { background: #28a745; color: white; }

.btn-sm {
  padding: 4px 10px;
  font-size: 12px;
  margin: 0 2px;
}

.btn-block {
  width: 100%;
}

.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: white;
}

.badge-success { background: #28a745; }
.badge-secondary { background: #6c757d; }

.d-flex { display: flex; }
.justify-content-between { justify-content: space-between; }
.align-items-center { align-items: center; }
.mb-4 { margin-bottom: 20px; }
.mt-3 { margin-top: 15px; }
.py-4 { padding: 40px 0; }
.text-center { text-align: center; }
.text-muted { color: #6c757d; }
.card { background: white; border-radius: 8px; }
.card-body { padding: 20px; }
.card-title { font-size: 18px; margin-bottom: 10px; }
.card-text { font-size: 14px; }

.row {
  display: flex;
  flex-wrap: wrap;
  margin: 0 -10px;
}

.col-md-6 { flex: 0 0 50%; padding: 0 10px; }
.col-lg-4 { flex: 0 0 33.333%; }

.spinner-border {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .col-md-6 { flex: 0 0 100%; }
}
</style>